import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Footer from "../components/site/Footer";
import { CONTACT } from "../data/site";

const LAST_UPDATED = "December 3, 2025";

export default function Privacy() {
  useEffect(() => {
    document.title = "Privacy Policy · Cresta Bath & Tile";
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid="privacy-page" className="bg-[#0A0A0A] text-[#F5F5F0] min-h-screen">
      <header className="border-b border-white/5 bg-[#0A0A0A]/85 backdrop-blur-xl">
        <div className="max-w-3xl mx-auto px-6 md:px-12 py-6 flex items-center justify-between">
          <Link
            to="/"
            data-testid="privacy-back-home"
            className="font-serif-display text-xl md:text-2xl text-[#F5F5F0]"
          >
            Cresta <span className="italic text-champagne">Bath & Tile</span>
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-[12px] tracking-[0.2em] uppercase text-[#A3A3A3] hover:text-champagne font-sans-body"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 md:px-12 py-20 md:py-28">
        <span className="overline">Legal</span>
        <h1 className="font-serif-display mt-5 text-4xl sm:text-5xl md:text-6xl text-[#F5F5F0] leading-tight">
          Privacy <span className="italic text-champagne">Policy</span>
        </h1>
        <p className="mt-4 text-[12px] tracking-[0.22em] uppercase text-[#A3A3A3] font-sans-body">
          Last updated · {LAST_UPDATED}
        </p>

        <div className="mt-12 md:mt-16 space-y-12 font-sans-body text-[#D4D4D4] leading-relaxed">
          <Section title="Who we are">
            <p>
              Cresta Bath & Tile ("we", "us", "our") is a bathroom renovation contractor based in Ottawa, Ontario,
              Canada, serving homeowners across the Greater Ottawa Area. This Privacy Policy describes how we
              collect, use, disclose and protect your personal information in accordance with the Personal
              Information Protection and Electronic Documents Act (PIPEDA) and applicable Ontario privacy law.
            </p>
          </Section>

          <Section title="Information we collect">
            <p>When you request a free estimate through our website, we collect only the information you choose to provide:</p>
            <List
              items={[
                "Full name",
                "Phone number",
                "Email address",
                "Neighborhood or general address area (to determine service area and travel)",
                "Project details: type of work, budget range, timeline, and any notes you provide",
              ]}
            />
            <p className="mt-4">
              We also automatically collect limited technical information when you visit the site — including IP
              address, browser type, device type, referring URL, pages visited and approximate timing — through
              first- and third-party cookies and similar technologies described below.
            </p>
          </Section>

          <Section title="Why we collect it">
            <p>
              We collect your personal information for one primary purpose: <span className="text-[#F5F5F0]">to respond to your estimate request and deliver our renovation services.</span> Specifically:
            </p>
            <List
              items={[
                "Contact you about your estimate, scheduling and project follow-up",
                "Prepare and send you a written quote",
                "Communicate during the project (scheduling, materials, change orders)",
                "Provide post-installation warranty support",
                "Improve our website experience and measure marketing effectiveness in aggregate",
              ]}
            />
            <p className="mt-4">
              We use your information only for these purposes. We will obtain your additional consent before using
              your information for any new purpose not listed here.
            </p>
          </Section>

          <Section title="Cookies, pixels and advertising">
            <p>
              Our website uses cookies and similar technologies (including conversion pixels from Meta/Facebook
              and Google) to:
            </p>
            <List
              items={[
                "Keep the site functional (e.g., remember form progress)",
                "Measure how visitors find and interact with our pages",
                "Help us optimize our advertising and reach homeowners similar to our customers",
              ]}
            />
            <p className="mt-4">
              These tools may collect device identifiers and aggregated behavioural data. You can disable cookies
              in your browser settings or opt out of personalized advertising at
              <a
                href="https://www.youradchoices.ca/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-champagne hover:underline-grow ml-1"
              >
                youradchoices.ca
              </a>{" "}
              (Canada),{" "}
              <a
                href="https://www.facebook.com/about/ads"
                target="_blank"
                rel="noopener noreferrer"
                className="text-champagne hover:underline-grow"
              >
                Meta Ad Preferences
              </a>{" "}
              and{" "}
              <a
                href="https://adssettings.google.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-champagne hover:underline-grow"
              >
                Google Ads Settings
              </a>
              .
            </p>
          </Section>

          <Section title="How we share it">
            <p>
              <span className="text-[#F5F5F0]">We do not sell, rent, or trade your personal information.</span> We
              do not share it with third parties for their own marketing purposes.
            </p>
            <p className="mt-4">
              We use a small number of trusted service providers to operate this website and our business —
              including hosting infrastructure, email delivery (Resend), and analytics. These providers process
              your information only on our instructions and under written confidentiality terms.
            </p>
            <p className="mt-4">
              We may disclose information when required by law, court order or to protect our legal rights, but
              never beyond what the law requires.
            </p>
          </Section>

          <Section title="How long we keep it">
            <p>
              We retain your estimate request and project records for as long as needed to provide our services and
              warranty support (typically up to seven years after the project completes, in line with Canadian
              record-keeping practice). You can ask us to delete your information sooner — see "Your rights" below.
            </p>
          </Section>

          <Section title="How we protect it">
            <p>
              We use industry-standard safeguards including encrypted connections (HTTPS), access controls,
              password-protected admin tools, and trusted Canadian/North American cloud infrastructure. No system
              is perfectly secure, but we work to keep your information protected against unauthorized access,
              loss or misuse.
            </p>
          </Section>

          <Section title="Your rights">
            <p>Under PIPEDA you have the right to:</p>
            <List
              items={[
                "Access the personal information we hold about you",
                "Request correction of inaccurate information",
                "Withdraw consent and request deletion (subject to legal and contractual record-keeping requirements)",
                "Ask questions or make a complaint about how we handle your information",
              ]}
            />
            <p className="mt-4">
              To exercise any of these rights, contact us using the information below. We respond to all requests
              within 30 days.
            </p>
          </Section>

          <Section title="Children">
            <p>
              Our services are intended for homeowners. We do not knowingly collect personal information from
              individuals under 18. If you believe a child has submitted information through our site, contact us
              and we will delete it promptly.
            </p>
          </Section>

          <Section title="Changes to this policy">
            <p>
              We may update this Privacy Policy from time to time. The "Last updated" date at the top reflects the
              most recent revision. Material changes will be highlighted on our website.
            </p>
          </Section>

          <Section title="Contact us">
            <p>
              For any privacy question, access request, correction, or complaint, please reach our Privacy Officer:
            </p>
            <div className="mt-6 border border-[#262626] bg-[#0C0C0C] p-6 md:p-8 rounded-sm space-y-2 text-[#F5F5F0]">
              <div className="font-serif-display text-xl">Cresta Bath & Tile</div>
              <div className="text-[#A3A3A3]">{CONTACT.area}, Ontario, Canada</div>
              <div className="pt-2">
                Email:{" "}
                <a href={CONTACT.emailHref} className="text-champagne hover:underline-grow">
                  {CONTACT.email}
                </a>
              </div>
              <div>
                Phone:{" "}
                <a href={CONTACT.phoneHref} className="text-champagne hover:underline-grow">
                  {CONTACT.phone}
                </a>
              </div>
            </div>
            <p className="mt-6 text-sm text-[#A3A3A3]">
              If you are not satisfied with our response, you may also contact the Office of the Privacy
              Commissioner of Canada at{" "}
              <a
                href="https://www.priv.gc.ca/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-champagne hover:underline-grow"
              >
                priv.gc.ca
              </a>
              .
            </p>
          </Section>
        </div>
      </main>

      <Footer />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-serif-display text-2xl md:text-3xl text-[#F5F5F0] mb-4">{title}</h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function List({ items }) {
  return (
    <ul className="mt-4 space-y-2.5 pl-1">
      {items.map((item) => (
        <li key={item} className="flex gap-3">
          <span className="text-champagne mt-2 w-1 h-1 rounded-full bg-champagne shrink-0" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}
